import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, userProfile, signOut } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';

  useEffect(() => {
    if (user && userProfile?.isBlocked) {
      toast({
        title: 'Access Denied',
        description: 'Your account has been blocked. Please contact support.',
        variant: 'destructive',
      });
      signOut();
    }
  }, [user, userProfile?.isBlocked, signOut, toast]);

  if (isDemo) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user signed up with email/password and hasn't verified
  const isPasswordProvider = user.providerData.some(p => p.providerId === 'password');
  if (isPasswordProvider && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check 2FA Gate
  if (userProfile?.mfaEnabled) {
    const isVerified = sessionStorage.getItem(`user_2fa_verified_${user.uid}`) === 'true';
    if (!isVerified) {
      return <Navigate to="/verify-2fa" state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
}
